'use client';

import { useEffect, useRef, useState } from 'react';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { localAuthEnabled } from '@/lib/local-auth';

export function SignInModal({ returnTo, onClose, onGoogle }: {
  returnTo: string;
  onClose: () => void;
  onGoogle: (next: string) => Promise<void>;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const local = localAuthEnabled();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [create, setCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    dialog.current?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, []);

  async function google() {
    setBusy(true); setError('');
    try { await onGoogle(returnTo); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not sign in.'); }
    finally { setBusy(false); }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const client = getSupabaseBrowserClient();
      if (!client || !local) throw new Error('Local authentication is not configured.');
      const result = create ? await client.auth.signUp({ email, password }) : await client.auth.signInWithPassword({ email, password });
      if (result.error) throw result.error;
      if (!result.data.session) throw new Error('Check your local email inbox to confirm your account.');
      window.location.replace(returnTo);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not sign in.'); }
    finally { setBusy(false); }
  }

  return <dialog ref={dialog} aria-labelledby="signin-title" aria-describedby="signin-description"
    className="m-auto max-h-[calc(100svh-2rem)] w-[calc(100%-2rem)] max-w-sm overflow-y-auto rounded-2xl border border-border bg-card p-6 text-foreground shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-[1px] sm:p-8"
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    <button onClick={onClose} aria-label="Close sign-in" className="ui-button ui-button--ghost ui-button--icon absolute right-3 top-3">
      <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 6 12 12M18 6 6 18" /></svg>
    </button>
    <div className="mb-6 text-center">
      <h2 id="signin-title" className="mt-4 font-serif text-2xl font-semibold">Sign in to SlideToAnki</h2>
      <p id="signin-description" className="mt-2 text-sm leading-6 text-muted">Your decks and progress in one place.</p>
    </div>
    <button onClick={google} disabled={busy} className="ui-button ui-button--primary w-full">{busy ? 'Please wait…' : 'Sign in with Google'}</button>
    <p className="mt-4 text-center text-xs leading-5 text-muted">New here? Signing in creates your free account.</p>
    {error && <p role="alert" className="mt-4 text-sm text-red-500">{error}</p>}
    {local && <details className="mt-5">
      <summary className="cursor-pointer text-center text-sm text-muted">Use a local test account</summary>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <label className="block text-sm">Email<input type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-border bg-background px-3" /></label>
        <label className="block text-sm">Password<input type="password" autoComplete={create ? 'new-password' : 'current-password'} minLength={10} required value={password} onChange={event => setPassword(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-border bg-background px-3" /></label>
        <p className="text-xs text-muted">Use a test-only password of at least 10 characters.</p>
        <button disabled={busy} className="ui-button ui-button--primary w-full">{busy ? 'Please wait…' : create ? 'Create local account' : 'Sign in'}</button>
      </form>
      <button className="ui-button ui-button--ghost mt-4 w-full text-primary underline" disabled={busy} onClick={() => { setCreate(!create); setError(''); }}>{create ? 'Already have a local account? Sign in' : 'Create a local account'}</button>
    </details>}
  </dialog>;
}
